import type {
  ParkedOrderSnapshot,
  ParkedOrderLine,
  ParkedOrderLineModifier,
  ParkedOrderCustomer,
} from '@matix/types';
import { roundMoney } from '@matix/utils';
import { moneyPrecision } from '../../store/currency';

/**
 * Inputs to `buildSnapshotFromCart`. The shape mirrors the cashier cart
 * store but with primitive types only — keeps this module independent of
 * the cart store's internal types so it can be unit-tested without zustand.
 */
export interface BuildSnapshotInput {
  lines: Array<{
    product: {
      id: number;
      code: string;
      name: string | null;
      nameAr: string | null;
      /** The unit the line sells in, and its names on a multi-unit item. */
      unitId?: number | null;
      unitName?: string | null;
      unitNameAr?: string | null;
    };
    qty: number;
    unitPrice: number;
    note?: string | null;
    unit_id?: number | null;
    /**
     * Selected modifiers, straight off the cart line (`CartLine.mods`). The
     * cart's `idx` is deliberately NOT carried — it is a position within the
     * modifier group's option list, regenerated on recall exactly as
     * `hydrateFromReceipt` does.
     */
    mods?: Array<{
      option_id: number;
      group_id: number;
      group: string;
      name: string;
      nameAr: string;
      price: number;
      qty?: number;
    }>;
  }>;
  customer: {
    id: number;
    type: 'cash' | 'credit';
    name: string | null;
    name_ar: string | null;
    phone: string | null;
  } | null;
  delivery: { fee: number; zone?: string | null; address?: string | null } | null;
  discount: { type: 'amount' | 'percent'; value: number } | null;
  orderType: 'walkin' | 'takeaway' | 'delivery' | 'pickup';
  note?: string | null;
  referenceNo?: string | null;
  /**
   * Phase 9 — delivery draft preserved across park/recall. Pass through
   * verbatim when present; the server stores the whole snapshot as JSON
   * and doesn't interpret this field.
   */
  deliveryDraft?: {
    customer_id: number | null;
    address_id: number | null;
    driver_id: number | null;
    delivery_fees: number;
    delivery_date: string;
    reference_no: string;
  } | null;
}

/**
 * Build a ParkedOrderSnapshot from the cart store's current state.
 *
 * Pure function — no React, no I/O. The caller (Park button click handler)
 * gathers the inputs from useCart selectors and passes them in.
 */
export function buildSnapshotFromCart(input: BuildSnapshotInput): ParkedOrderSnapshot {
  const lines: ParkedOrderLine[] = input.lines.map((line) => {
    const modifiers: ParkedOrderLineModifier[] = (line.mods ?? []).map((m) => ({
      option_id: m.option_id,
      group_id: m.group_id,
      group: m.group,
      name: m.name,
      name_ar: m.nameAr,
      price: m.price,
      ...(m.qty != null ? { qty: m.qty } : {}),
    }));
    return {
      product_id: line.product.id,
      product_code: line.product.code,
      // Cart products may have null names; fall back to code so the parked
      // card never shows an empty line.
      product_name: line.product.name ?? line.product.code,
      product_name_ar: line.product.nameAr ?? '',
      qty: line.qty,
      unit_price: line.unitPrice,
      unit_id: line.unit_id ?? line.product.unitId ?? null,
      // Names only when the product carries them (a multi-unit item), so the
      // stored JSON of every other line is exactly what it was.
      ...(line.product.unitName || line.product.unitNameAr
        ? { unit_name: line.product.unitName ?? null, unit_name_ar: line.product.unitNameAr ?? null }
        : {}),
      note: line.note ?? null,
      // Omitted entirely when the line has none — keeps the stored JSON the
      // same size it was for the overwhelming majority of lines.
      ...(modifiers.length > 0 ? { modifiers } : {}),
    };
  });

  const customer: ParkedOrderCustomer | null = input.customer
    ? {
        customer_id: input.customer.id,
        type: input.customer.type,
        name: input.customer.name,
        name_ar: input.customer.name_ar,
        phone: input.customer.phone,
      }
    : null;

  return {
    v: 1,
    lines,
    customer,
    delivery: input.delivery,
    discount: input.discount,
    order_type: input.orderType,
    note: input.note ?? null,
    reference_no: input.referenceNo ?? null,
    ...(input.deliveryDraft !== undefined ? { delivery_draft: input.deliveryDraft } : {}),
  };
}

/**
 * Compute the display fields the parked-order card shows. Used:
 *   1. By the Park button to label the optimistic local row before the
 *      server responds with its own derived label.
 *   2. As a sanity check in tests.
 *
 * Mirrors the server's `summarizeSnapshot` so the optimistic and synced
 * labels are visually identical (no flicker on confirm).
 */
export function summarizeForCard(snapshot: ParkedOrderSnapshot): {
  customerLabel: string | null;
  itemCount: number;
  totalValue: number;
} {
  const customerLabel = (() => {
    if (!snapshot.customer) return 'Walk-in';
    const parts: string[] = [];
    const name = snapshot.customer.name || snapshot.customer.name_ar;
    if (name) parts.push(name);
    if (snapshot.customer.phone) parts.push(snapshot.customer.phone);
    if (snapshot.customer.type === 'credit') parts.push('(credit)');
    return parts.join(' / ') || null;
  })();

  const itemCount = snapshot.lines.length;

  const subtotal = snapshot.lines.reduce(
    (s, l) => s + round2(l.qty * l.unit_price),
    0,
  );
  const discountValue =
    snapshot.discount?.type === 'amount'
      ? snapshot.discount.value
      : snapshot.discount?.type === 'percent'
        ? round2(subtotal * (snapshot.discount.value / 100))
        : 0;
  const deliveryFee = snapshot.delivery?.fee ?? 0;
  const totalValue = round2(subtotal - discountValue + deliveryFee);

  return { customerLabel, itemCount, totalValue };
}

/**
 * "5 minutes ago" / "2 hours ago" formatter for the card grid. Returns the
 * absolute time once the parked order is older than 24 hours so cashiers
 * can spot stale orders at a glance.
 */
export function formatTimeAgo(parkedAt: string, now: number = Date.now()): string {
  const t = new Date(parkedAt).getTime();
  if (!Number.isFinite(t)) return '';
  const diffMs = Math.max(0, now - t);
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  // After a day, show the local time of day so the cashier can identify
  // which shift the parked order came from at a glance.
  return new Date(parkedAt).toLocaleString();
}

/**
 * Round money at the tenant currency's precision — one rounding rule across
 * the app (see store/cart.ts and @matix/utils/money).
 */
function round2(n: number): number {
  return roundMoney(n, moneyPrecision());
}
